/**
 * Custom hook for managing Plaid holdings data.
 *
 * Handles fetching holdings from connected brokerage accounts
 * and manages connection state. Includes smart caching with
 * Firebase Remote Config for TTL and daily refresh limits.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  getPlaidCredentials,
  clearPlaidCredentials,
  savePlaidPortfolio,
  savePlaidPortfolios,
  getPlaidHoldingsCache,
  savePlaidHoldingsCache,
  clearPlaidHoldingsCache,
  clearPlaidSnapshots,
  PlaidHoldingsCache,
  PlaidAccountForPortfolio,
  addPlaidSnapshot,
  getPlaidPortfolioAccountMap,
} from '../storage';
import { getPlaidHoldings, disconnectPlaid as disconnectPlaidBackend, PlaidHolding, PlaidAccount } from '../apis/plaid';
import { usePlaidCacheConfig } from './useRemoteConfig';

interface CacheStatus {
  isFromCache: boolean;
  cacheAgeMinutes: number | null;
  isStale: boolean;
  refreshesRemaining: number;
  canRefresh: boolean;
}

interface UsePlaidHoldingsResult {
  holdings: PlaidHolding[];
  accounts: PlaidAccount[];
  totalValue: number;
  unsupportedSymbols: string[];
  isLoading: boolean;
  isConnected: boolean;
  institutionName: string | null;
  /** @deprecated Use portfolioIds instead */
  portfolioId: string | null;
  portfolioIds: string[];
  /** Map of portfolioId -> Plaid accountId for filtering holdings */
  portfolioAccountMap: Record<string, string>;
  error: string | null;
  cacheStatus: CacheStatus;
  fetchHoldings: (forceRefresh?: boolean) => Promise<void>;
  disconnect: () => Promise<void>;
  refetch: () => Promise<void>;
}

const getTodayDate = (): string => new Date().toISOString().split('T')[0];

export const usePlaidHoldings = (): UsePlaidHoldingsResult => {
  const [holdings, setHoldings] = useState<PlaidHolding[]>([]);
  const [accounts, setAccounts] = useState<PlaidAccount[]>([]);
  const [totalValue, setTotalValue] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [institutionName, setInstitutionName] = useState<string | null>(null);
  const [portfolioId, setPortfolioId] = useState<string | null>(null);
  const [portfolioIds, setPortfolioIds] = useState<string[]>([]);
  const [portfolioAccountMap, setPortfolioAccountMap] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [unsupportedSymbols, setUnsupportedSymbols] = useState<string[]>([]);

  // Cache status state
  const [isFromCache, setIsFromCache] = useState<boolean>(false);
  const [cacheAgeMinutes, setCacheAgeMinutes] = useState<number | null>(null);
  const [isStale, setIsStale] = useState<boolean>(false);
  const [refreshesRemaining, setRefreshesRemaining] = useState<number>(0);

  // Get cache config from Firebase Remote Config
  const cacheConfig = usePlaidCacheConfig();
  // Store config in ref to avoid re-creating fetchHoldings on config changes
  const cacheConfigRef = useRef(cacheConfig);
  cacheConfigRef.current = cacheConfig;

  /**
   * Build per-account portfolio data and save portfolios.
   * Groups holdings by accountId and creates one portfolio per account.
   */
  const savePerAccountPortfolios = useCallback(
    async (holdingsData: PlaidHolding[], accountsData: PlaidAccount[], instName: string): Promise<string[]> => {
      // Build account map for quick lookup
      const accountMap = new Map(accountsData.map((a) => [a.accountId, a]));

      // Group holdings by accountId
      const holdingsByAccount = new Map<string, PlaidHolding[]>();
      for (const h of holdingsData) {
        const accId = h.accountId || 'unknown';
        const list = holdingsByAccount.get(accId) || [];
        list.push(h);
        holdingsByAccount.set(accId, list);
      }

      // Build per-account data
      const accountPortfolios: PlaidAccountForPortfolio[] = [];
      for (const [accId, accHoldings] of holdingsByAccount) {
        const account = accountMap.get(accId);
        const symbols = accHoldings.map((h) => h.symbol).filter((s) => s && s !== 'N/A');
        accountPortfolios.push({
          accountId: accId,
          name: account?.name || 'Account',
          subtype: account?.subtype || null,
          symbols,
        });
      }

      if (accountPortfolios.length === 0) {
        return [];
      }

      return savePlaidPortfolios(instName || 'Plaid Portfolio', accountPortfolios);
    },
    [],
  );

  const fetchHoldings = useCallback(
    async (forceRefresh = false) => {
      try {
        const credentials = await getPlaidCredentials();

        // Check if connected using deviceUuid (itemId indicates active connection)
        if (!credentials.deviceUuid || !credentials.itemId) {
          setIsConnected(false);
          setHoldings([]);
          setAccounts([]);
          setTotalValue(0);
          setPortfolioId(null);
          setIsFromCache(false);
          setCacheAgeMinutes(null);
          setIsStale(false);
          return;
        }

        setIsLoading(true);
        setError(null);
        setInstitutionName(credentials.institutionName);

        const { ttlMinutes, maxDailyRefreshes, cacheEnabled } = cacheConfigRef.current;
        const cache = await getPlaidHoldingsCache();
        const now = Date.now();
        const today = getTodayDate();

        // Reset counter if new day
        let refreshCountToday = cache?.refreshCountToday || 0;
        if (cache && cache.refreshDate !== today) {
          refreshCountToday = 0;
        }

        // Calculate cache validity
        // Also invalidate cache if it's missing unsupportedSymbols or accountId (old cache formats)
        const cacheAge = cache ? (now - cache.lastFetched) / 1000 / 60 : Infinity;
        const cacheHasUnsupportedSymbols = cache && 'unsupportedSymbols' in cache;
        const cacheHasAccountId = !cache || cache.holdings.length === 0 || 'accountId' in cache.holdings[0];
        const cacheValid = cache && cacheAge < ttlMinutes && cacheHasUnsupportedSymbols && cacheHasAccountId;
        const canRefresh = refreshCountToday < maxDailyRefreshes;

        // Update refreshes remaining
        setRefreshesRemaining(Math.max(0, maxDailyRefreshes - refreshCountToday));

        // Use cache if valid and not forcing refresh
        if (cacheEnabled && cacheValid && !forceRefresh) {
          setHoldings(cache.holdings as PlaidHolding[]);
          setAccounts(cache.accounts as PlaidAccount[]);
          setTotalValue(cache.totalValue);
          setUnsupportedSymbols(cache.unsupportedSymbols || []);
          setIsConnected(true);
          setIsFromCache(true);
          setCacheAgeMinutes(Math.round(cacheAge));
          setIsStale(false);

          // Still update per-account portfolios
          const ids = await savePerAccountPortfolios(
            cache.holdings as PlaidHolding[],
            cache.accounts as PlaidAccount[],
            credentials.institutionName || 'Plaid Portfolio',
          );
          setPortfolioIds(ids);
          setPortfolioId(ids[0] || null);
          setPortfolioAccountMap(await getPlaidPortfolioAccountMap());
          return;
        }

        // If can't refresh, use stale cache if available (only if cache has valid format)
        if (!canRefresh && cache && cacheHasAccountId) {
          setHoldings(cache.holdings as PlaidHolding[]);
          setAccounts(cache.accounts as PlaidAccount[]);
          setTotalValue(cache.totalValue);
          setUnsupportedSymbols(cache.unsupportedSymbols || []);
          setIsConnected(true);
          setIsFromCache(true);
          setCacheAgeMinutes(Math.round(cacheAge));
          setIsStale(true);

          const ids = await savePerAccountPortfolios(
            cache.holdings as PlaidHolding[],
            cache.accounts as PlaidAccount[],
            credentials.institutionName || 'Plaid Portfolio',
          );
          setPortfolioIds(ids);
          setPortfolioId(ids[0] || null);
          setPortfolioAccountMap(await getPlaidPortfolioAccountMap());
          return;
        }

        // Fetch fresh data from API using deviceUuid (access token is retrieved server-side)
        const data = await getPlaidHoldings(credentials.deviceUuid);

        setHoldings(data.holdings);
        setAccounts(data.accounts);
        setTotalValue(data.totalValue);
        setUnsupportedSymbols(data.unsupportedSymbols);
        setIsConnected(true);
        setIsFromCache(false);
        setCacheAgeMinutes(0);
        setIsStale(false);

        // Save to cache
        const newRefreshCount = refreshCountToday + 1;
        const cacheData: PlaidHoldingsCache = {
          holdings: data.holdings.map((h) => ({
            accountId: h.accountId,
            symbol: h.symbol,
            name: h.name,
            quantity: h.quantity,
            costBasis: h.costBasis,
            currentValue: h.currentValue,
            type: h.type,
            gainLoss: h.gainLoss,
            gainLossPercent: h.gainLossPercent,
          })),
          accounts: data.accounts.map((a) => ({
            accountId: a.accountId,
            name: a.name,
            type: a.type,
            subtype: a.subtype || '',
            mask: a.mask,
          })),
          totalValue: data.totalValue,
          lastFetched: now,
          refreshCountToday: newRefreshCount,
          refreshDate: today,
          unsupportedSymbols: data.unsupportedSymbols,
        };
        await savePlaidHoldingsCache(cacheData);
        setRefreshesRemaining(Math.max(0, maxDailyRefreshes - newRefreshCount));

        // Save snapshot for historical chart
        const snapshot = {
          date: new Date().toISOString(),
          totalValue: data.totalValue,
        };
        await addPlaidSnapshot(snapshot);

        // Create or update per-account portfolios
        const ids = await savePerAccountPortfolios(
          data.holdings,
          data.accounts,
          credentials.institutionName || 'Plaid Portfolio',
        );
        setPortfolioIds(ids);
        setPortfolioId(ids[0] || null);
        setPortfolioAccountMap(await getPlaidPortfolioAccountMap());
      } catch (err) {
        console.error('Error fetching Plaid holdings:', err);
        setError('Failed to fetch holdings. Please try reconnecting your account.');

        // Check for auth errors that indicate invalid/expired token
        if (axios.isAxiosError(err)) {
          const status = err.response?.status;
          // DRF CamelCaseJSONRenderer transforms error_code to errorCode
          const errorCode = err.response?.data?.errorCode || err.response?.data?.error_code;

          // Clear credentials on auth failures
          if (status === 401 || errorCode === 'INVALID_ACCESS_TOKEN' || errorCode === 'ITEM_LOGIN_REQUIRED') {
            await clearPlaidCredentials();
            await clearPlaidHoldingsCache();
            setIsConnected(false);
          }
        }
      } finally {
        setIsLoading(false);
      }
    },
    [savePerAccountPortfolios],
  ); // savePerAccountPortfolios is stable (no deps)

  const disconnect = useCallback(async () => {
    // Get deviceUuid to disconnect from backend
    const credentials = await getPlaidCredentials();

    if (credentials.deviceUuid) {
      try {
        await disconnectPlaidBackend(credentials.deviceUuid);
      } catch (e) {
        // Continue with local cleanup even if backend fails
      }
    }

    // Clear credentials, cache, and local snapshots
    // deviceUuid is preserved to maintain backend snapshot history
    await clearPlaidCredentials();
    await clearPlaidHoldingsCache();
    // Clear local snapshots to prevent stale data if user reconnects to a different brokerage
    // and the backend API fails (which would fall back to local snapshots)
    await clearPlaidSnapshots();
    setHoldings([]);
    setAccounts([]);
    setTotalValue(0);
    setIsConnected(false);
    setInstitutionName(null);
    setPortfolioId(null);
    setPortfolioIds([]);
    setPortfolioAccountMap({});
    setError(null);
    setIsFromCache(false);
    setCacheAgeMinutes(null);
    setIsStale(false);
  }, []);

  // Force refresh wrapper
  const refetch = useCallback(async () => {
    await fetchHoldings(true);
  }, [fetchHoldings]);

  // Check connection status and fetch holdings on mount
  useEffect(() => {
    const checkConnection = async () => {
      const credentials = await getPlaidCredentials();
      // Check using itemId (accessToken is stored server-side only)
      if (credentials.itemId && credentials.deviceUuid) {
        setIsConnected(true);
        setInstitutionName(credentials.institutionName);
        await fetchHoldings();
      }
    };

    checkConnection();
  }, [fetchHoldings]);

  // Memoize cache status object
  const cacheStatus = useMemo<CacheStatus>(
    () => ({
      isFromCache,
      cacheAgeMinutes,
      isStale,
      refreshesRemaining,
      canRefresh: refreshesRemaining > 0,
    }),
    [isFromCache, cacheAgeMinutes, isStale, refreshesRemaining],
  );

  return {
    holdings,
    accounts,
    totalValue,
    unsupportedSymbols,
    isLoading,
    isConnected,
    institutionName,
    portfolioId,
    portfolioIds,
    portfolioAccountMap,
    error,
    cacheStatus,
    fetchHoldings,
    disconnect,
    refetch,
  };
};
